import Image from 'next/image';
import styled from 'styled-components';
import Title from '@/components/Title/Title';
import Button, { IButtonProps } from '@/components/Button/Button';
import { ITitle } from '@/components/BuilderIO/common/CommonFields';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';

const Container = styled.section`
  align-items: center;
  display: flex;
  color: #183e40;
  flex-direction: column;
  padding: 0px 8px 0px;
  width: 100%;

  .threeAcross_contentContainer {
    align-items: center;
    display: flex;
    flex-direction: column;
    // gap: 29px;
    max-width: 1172px;
  }

  h3 {
    margin-top: 24px;
    margin-bottom: 8px;
  }

  p {
    line-height: 19.12px;
    margin: 0px;
  }

  a.threeAcross_item:hover {
    color: inherit;
    text-decoration: none;
  }

  .threeAcross_items,
  .threeAcross_item {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .threeAcross_items {
    align-items: center;
    gap: 24px;
  }

  .threeAcross_item {
    max-width: 304px;
  }

  .threeAcross_item a {
    align-self: center;
    padding: 12px 25px;
    margin-top: 16px;
    min-height: 45px;
    min-width: auto;
    max-width: 166px;
  }

  .threeAcross_item_image {
    align-self: center;
    height: 128px;
    width: 128px;
  }

  .threeAcross_item_image img {
    height: inherit;
    object-fit: scale-down;
    width: 100%;
  }

  @media screen and (min-width: 768px) {
    padding: 0px 8px 0px;

    h3 {
      margin-bottom: 16px;
    }

    p {
      font-size: 16px;
      line-height: 21.86px;
    }

    .threeAcross_contentContainer {
      // gap: 24px;
    }

    .threeAcross_items {
      gap: 32px;
    }

    .threeAcross_item {
      max-width: 369px;
    }
  }

  @media screen and (min-width: 1024px) {
    .threeAcross_contentContainer {
      // gap: 32px;
    }

    .threeAcross_items {
      flex-direction: row;
      justify-content: center;
      gap: 33px;
    }

    .threeAcross_item {
      min-height: 305px;
    }
  }
`;

interface ThreeAcrossProps {
  /**
   * Optional header.
   * - content*: The content of the header
   * - titleHTMLTag: Heading level that will be used to render the optional header. Defaults to 'h2'
   * - titleSize: Select one of the styles 'extraLarge' | 'large' | 'medium' | 'small'.
   * - titleWeight: Optional header weight. Defaults to 'normal'
   */
  title?: ITitle;
  /**
   * How the item content should be aligned?
   */
  alignment?: 'left' | 'center' | 'right';
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * The list of items.
   * Props.
   * - title*: The item title
   * - content*: The main content of the item
   * - image*: The url of the item image
   * - alt*: Alternative text of the image
   * - link: Optional link for the item
   */
  items: ThreeAcrossItemProps[];
}

interface ThreeAcrossItemProps {
  title: ITitle;
  content: string;
  image: string;
  alt: string;
  link: string;
  optionalButton?: IButtonProps;
}

export default function ThreeAcross({ title, alignment, margin = false, anchorLinkID, items }: ThreeAcrossProps) {
  return (
    <Container {...getAnchorId(anchorLinkID)} className={getComponentMargin(margin)}>
      <div className="threeAcross_contentContainer">
        {title?.content && (
          <Title alignment={alignment} weight={title.titleWeight} component={title.titleHTMLTag} size={title.titleSize}>
            {title.content}
          </Title>
        )}
        <div className="threeAcross_items">
          {(items || []).map((item: ThreeAcrossItemProps, index: number) => {
            if (item.link) {
              return (
                <a href={item.link} key={index} className="threeAcross_item">
                  {item.image && (
                    <div className="threeAcross_item_image">
                      <Image alt={item.alt || ''} src={item.image} loading="lazy" width={128} height={128} />
                    </div>
                  )}
                  <Title
                    component={item.title.titleHTMLTag}
                    size={item.title.titleSize}
                    weight={item.title.titleWeight}
                    alignment={alignment || 'left'}
                    styles={{ margin: '0px' }}
                  >
                    {item.title.content}
                  </Title>
                  <p
                    style={{ textAlign: alignment || 'left' }}
                    className="tw-font-book tw-text-be-sm desktop:tw-text-be-base tw-text-be-dark-gray tw-m-0"
                  >
                    {item.content}
                  </p>
                  {item.optionalButton && item.optionalButton?.text && (
                    <Button {...item.optionalButton} border="black" />
                  )}
                </a>
              );
            }

            return (
              <div key={index} className="threeAcross_item">
                {item.image && (
                  <div className="threeAcross_item_image">
                    <img src={item.image} alt={item.alt} width={128} height={128} loading="lazy" />
                  </div>
                )}
                {item && item.title && (
                  <Title
                    component={item.title.titleHTMLTag}
                    size={item.title.titleSize}
                    weight={item.title.titleWeight}
                    alignment={alignment || 'left'}
                    className="tw-m-0"
                  >
                    {item.title.content}
                  </Title>
                )}
                <p
                  style={{ textAlign: alignment || 'left' }}
                  className="tw-font-book tw-text-be-sm desktop:tw-text-be-base tw-text-be-dark-gray tw-m-0"
                >
                  {item.content}
                </p>
                {item.optionalButton && item.optionalButton?.text && <Button {...item.optionalButton} border="black" />}
              </div>
            );
          })}
        </div>
      </div>
    </Container>
  );
}
